import {
  PokemonType,
  SkillData,
  SkillType,
  SkillRangeType,
  SkillEvent_Vfx,
  SkillEvent_AnchoredInvoke,
  SkillEvent_Wait,
} from "types/battle-engine/battle";
import { parseSkillIdToName } from "../utils/parse-id-to-name";
import { FRONTAL_WAVE_3_5_PATTERN } from "../skill-patterns/frontal-wave-3-5";
import { msToTicks } from "../utils/ms-to-ticks";
import { StatusEffect, TeamConditionEffect, SkillTeamCondition, SkillMomentum } from "types/battle-engine/battle";
import { CONE_3_PATTERN } from "../skill-patterns/cone-3";

export const [
  ICE_WAVE,
  VACUUM_WAVE,
  HURRICANE,
  PSYWAVE,
  PHANTOM_FORCE,
  DRAGON_BREATH,
] = [
  {
    id: "ice-wave",
    description: "Fires a wave of ice that damages enemies in a line.",
    power: 20,
    pokemon_type: PokemonType.ICE,
    types: [SkillType.DAMAGE],
    cooldown_ticks: msToTicks(7000),
    invoke_duration_ticks: 10,
    range: 5,
    align_to_user_dir: true,
    cast_pattern: FRONTAL_WAVE_3_5_PATTERN(),
    sprite_path: "/assets/spells/tile-fx/ice-1.png",
    sprite_scale: 0.6,
    anim_loops: 1,
    anim_speed: 0.75,
    is_wave: true,
  },
  {
    id: "vacuum-wave",
    description: "Fires a quick vacuum wave to damage enemies in front.",
    power: 20,
    pokemon_type: PokemonType.FIGHTING,
    types: [SkillType.DAMAGE],
    cooldown_ticks: msToTicks(7000),
    charging_duration_ticks: 50,
    range: 3,
    align_to_user_dir: true,
    cast_pattern: FRONTAL_WAVE_3_5_PATTERN(),
    sprite_path: "/assets/spells/impact/impact-toon-c-red.png",
    sprite_scale: 1,
    is_wave: true,
    invoke_duration_ticks: 10,
    anim_loops: 1,
    anim_speed: 1,
  },
  {
    id: "hurricane",
    description:
      "Fires a wind wave. Knocks up enemies if a Flying-type ally is present.",
    power: 20,
    pokemon_type: PokemonType.FLYING,
    types: [SkillType.DAMAGE],
    cooldown_ticks: msToTicks(5000),
    range: 5,
    align_to_user_dir: true,
    cast_pattern: FRONTAL_WAVE_3_5_PATTERN(),
    sprite_path: "/assets/spells/tile-fx/flying-1b.png",
    sprite_scale: 1.5,
    is_wave: true,
    invoke_duration_ticks: 10,
    anim_loops: 1,
    anim_speed: 0.5,
    team_conditions: [
      {
        condition: {
          type: "min_ally_type_count",
          pokemon_type: PokemonType.FLYING,
          value: 1,
        },
        effect: {
          stat: "apply_status",
          value: 1,
          status_effects: [
            {
              effect: {
                status: StatusEffect.KNOCKUP,
                duration_ticks: msToTicks(1500),
                intensity: 1,
              },
              target: "target",
            },
          ],
        } as TeamConditionEffect,
      } as SkillTeamCondition,
    ],
  },
  {
    id: "psywave",
    description: "Fires a wave of psychic energy to damage enemies in front.",
    power: 20,
    pokemon_type: PokemonType.PSYCHIC,
    types: [SkillType.DAMAGE],
    cooldown_ticks: msToTicks(5000),
    range: 5,
    align_to_user_dir: true,
    cast_pattern: FRONTAL_WAVE_3_5_PATTERN(),
    sprite_path: "/assets/spells/tile-fx/psychic-5.png",
    sprite_scale: 1.5,
    is_wave: true,
    invoke_duration_ticks: 10,
    anim_loops: 1,
    anim_speed: 1,
  },
  {
    id: "phantom-force",
    description: "Attacks enemies from the shadows with a dark wave.",
    power: 20,
    pokemon_type: PokemonType.DARK,
    types: [SkillType.DAMAGE],
    cooldown_ticks: msToTicks(5000),
    range: 5,
    align_to_user_dir: true,
    cast_pattern: FRONTAL_WAVE_3_5_PATTERN(),
    sprite_path: "/assets/spells/tile-fx/dark-3.png",
    sprite_scale: 0.9,
    is_wave: true,
    invoke_duration_ticks: 10,
    anim_loops: 1,
    anim_speed: 0.75,
  },
  {
    id: "dragon-breath",
    description:
      "Fires draconic energy. Stacks reduce cooldown for consecutive uses.",
    power: 17,
    momentum: {
      max_stacks: 5,
      effects: [
        {
          type: "cooldown_mult",
          value_per_stack: 0.1,
        },
      ],
    } as SkillMomentum,
    pokemon_type: PokemonType.DRAGON,
    types: [SkillType.DAMAGE],
    cooldown_ticks: msToTicks(5000),
    range: 3,
    align_to_user_dir: true,
    cast_pattern: CONE_3_PATTERN(),
    sprite_path: "/assets/spells/tile-fx/dragon-1.png",
    sprite_scale: 1.25,
    is_wave: true,
    invoke_duration_ticks: 10,
    anim_loops: 1,
    anim_speed: 0.75,
  },
].map((skill) => ({
  id: skill.id,
  event_checks:
    "event_checks" in skill
      ? (skill.event_checks as SkillData["event_checks"])
      : undefined,
  momentum:
    "momentum" in skill ? (skill.momentum as SkillData["momentum"]) : undefined,
  team_conditions:
    "team_conditions" in skill
      ? (skill.team_conditions as SkillData["team_conditions"])
      : undefined,
  starts_active:
    "starts_active" in skill ? (skill.starts_active as boolean) : undefined,
  name: parseSkillIdToName(skill.id),
  description: skill.description || "",
  power: skill.power,
  pokemon_type: skill.pokemon_type,
  types: [SkillType.DAMAGE],
  cooldown_ticks: skill.cooldown_ticks,
  mana_cost: 15,
  range: {
    type: SkillRangeType.ANCHORED_INVOKE,
    value: skill.range,
  },
  charging: {
    is_required: true,
    align_to: "target",
    anim_on_end: {
      name: "special",
      dir: "target",
    },
    duration_ticks: skill.charging_duration_ticks ?? 35,
  } as const,
  align_to_user_dir: true,
  cast_pattern: skill.cast_pattern,
  on_cast: [
    {
      __typename: "Vfx",
      duration_ticks: skill.charging_duration_ticks ?? 35,
      target: {
        type: "self",
        id: 0,
      },
      status_effects: [],
      starting_tick: 0,
      sprite_path: "/assets/spells/impact/tiny-pink-1.png",
      blocks_event_pipeline: true,
    } as SkillEvent_Vfx,
    ...skill.cast_pattern.East.map(
      ([x, y, blocks]) =>
        ({
          __typename: "AnchoredInvoke",
          duration_ticks: skill.invoke_duration_ticks ?? 55,
          target: {
            type: "self",
            id: 0,
          },
          status_effects: [],
          starting_tick: 0,
          relative_x: x,
          relative_y: y,
          sprite_path: skill.sprite_path,
          sprite_scale: skill.sprite_scale ?? 1,
          anim_loops: skill.anim_loops ?? 1,
          anim_speed: skill.anim_speed ?? 1,
          damage: skill.power,
          blocks_event_pipeline: skill.is_wave ? blocks : false,
        }) as SkillEvent_AnchoredInvoke,
    ),
  ],
  on_hit: [
    {
      __typename: "Vfx",
      duration_ticks: 50,
      target: {
        type: "enemy",
        id: 0,
      },
      status_effects: [],
      starting_tick: 0,
      sprite_path: "/assets/spells/impact/tiny-white-1.png",
      blocks_event_pipeline: false,
    } as SkillEvent_Vfx,
  ],
}));